import { Button, ListItem, Typography } from '@mui/material';
import type { CSSProperties, FC, ReactNode } from 'react';
import { useMemo } from 'react';
import { Link, useLocation, useResolvedPath } from 'react-router-dom';

interface CabinetMenuLinkProps {
  to?: string;
  title: ReactNode;
  style?: CSSProperties;
  disabled?: boolean;
  onClick?: () => void;
}

const CabinetMenuLink: FC<CabinetMenuLinkProps> =
  ({
     to,
     title,
     style,
     disabled,
     onClick,
     ...props
   }) => {
    const path = useResolvedPath(to ?? '');
    const location = useLocation();
    const isActive = location.pathname.includes(path.pathname);
    const component = to ? Link : 'button';

    const onClickProp = useMemo(() => {
      if (to) return undefined;
      if (onClick) return onClick;

      return undefined;
    }, [to, onClick]);

    return (
      <ListItem style={{ ...style }} {...props} sx={{ p: 0 }}>
        <Button
          variant="text"
          component={component}
          to={to}
          replace
          disableElevation
          disableRipple={isActive}
          disableTouchRipple
          disableFocusRipple
          sx={(t) => ({
            display: 'flex',
            flexGrow: 1,
            flexDirection: 'column',
            alignItems: 'start',
            justifyContent: 'center',
            minHeight: 36,
            py: 0, px: 1,
            fontSize: t.typography.caption.fontSize,
            textDecoration: 'none',
            textTransform: 'capitalize',
            borderRadius: t.borderRadius.md,
            backgroundColor: isActive ? 'rgba(41, 121, 255, 0.08)' : 'transparent',
            color: isActive ? t.palette.primary.main : t.palette.text.primary,

            '&:hover': {
              color: t.palette.primary.main,
              backgroundColor: isActive ? 'rgba(41, 121, 255, 0.08)' : 'transparent',
            },
            '&:hover .MuiSvgIcon-root': {
              color: t.palette.primary.main,
            },
            '&[disabled]': {
              color: t.palette.text.disabled,
            },
          })}
          onClick={onClickProp}
          disabled={disabled}
        >

          <Typography
            sx={(t) => ({
              fontSize: t.typography.body2.fontSize,
              fontWeight: 400,
              letterSpacing: '0.15px',
            })}
          >
            {title}
          </Typography>
        </Button>
      </ListItem>
    );
  };

export default CabinetMenuLink;
